import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { useGame } from './GameContext';
import { ConnectionProvider, WalletProvider, useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { PhantomWalletAdapter, SolflareWalletAdapter } from '@solana/wallet-adapter-wallets';
import { clusterApiUrl, PublicKey } from '@solana/web3.js';

// Define Solana wallet interface
interface SolanaWallet {
  publicKey: string;
  isConnected: boolean;
}

interface Web3ContextType {
  account: string | null;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  balance: number;
  isSolana: boolean;
  fullAddress: string | null;
}

const Web3Context = createContext<Web3ContextType | undefined>(undefined);

export function useWeb3() {
  const context = useContext(Web3Context);
  if (context === undefined) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
}

interface Web3ProviderProps {
  children: ReactNode;
}

// Inner context provider that uses the wallet connection
const SolanaWalletContextProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const { setWalletConnected } = useGame();
  const { publicKey, connected, disconnect: solanaDisconnect, connect } = useWallet();
  const { connection } = useConnection();
  const [account, setAccount] = useState<string | null>(null);
  const [fullAddress, setFullAddress] = useState<string | null>(null);
  const [balance, setBalance] = useState<number>(0);
  const [isConnecting, setIsConnecting] = useState(false);

  // Update account info when publicKey changes
  useEffect(() => {
    if (publicKey) {
      const address = publicKey.toBase58();
      setFullAddress(address);
      const displayAddress = `${address.slice(0, 4)}...${address.slice(-4)}`;
      setAccount(displayAddress);
      // Fetch balance
      fetchBalance(publicKey);
    } else {
      setAccount(null);
      setFullAddress(null);
      setBalance(0);
    }
  }, [publicKey, connection]);

  // Update game state when connected changes
  useEffect(() => {
    setWalletConnected(connected, fullAddress);
  }, [connected, fullAddress, setWalletConnected]);

  // Fetch balance function
  const fetchBalance = async (publicKey: PublicKey) => {
    try {
      if (connection && publicKey) {
        const balance = await connection.getBalance(publicKey);
        setBalance(balance / 1000000000); // Convert lamports to SOL
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
      setBalance(0);
    }
  };

  // Connect wallet
  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      await connect();
    } catch (error) {
      console.error('Error connecting wallet:', error);
    } finally {
      setIsConnecting(false);
    }
  };
  
  // Enhanced disconnect function that clears localStorage
  const handleDisconnect = () => {
    // Save the current game state for this wallet before disconnecting
    // This happens automatically in the GameContext when wallet address changes
    
    // Clear UI-specific localStorage items but not game progress
    localStorage.removeItem('hasSeenProfileWelcome');
    // Add other UI-specific localStorage items that should be cleared
    
    // Update game state to indicate disconnection
    // Pass null as the address to indicate no wallet is connected
    setWalletConnected(false, null);
    
    // Call the Solana wallet disconnect function
    solanaDisconnect();
  };

  // Refresh balance periodically
  useEffect(() => {
    if (publicKey && connected) {
      // Initial fetch
      fetchBalance(publicKey);
      
      // Set up interval to fetch balance every 30 seconds
      const intervalId = setInterval(() => {
        fetchBalance(publicKey);
      }, 30000);
      
      // Clean up interval on unmount or when wallet changes
      return () => clearInterval(intervalId);
    }
  }, [publicKey, connected, connection]);

  // Value to provide
  const value = {
    account,
    fullAddress,
    isConnected: connected,
    connect: handleConnect,
    disconnect: handleDisconnect,
    balance,
    isSolana: true
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
};

// Outer provider that sets up the Solana connection and adapter
export const Web3Provider: React.FC<Web3ProviderProps> = ({ children }) => {
  // You can change to mainnet or custom RPC endpoints
  const network = WalletAdapterNetwork.Devnet;
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  // Initialize all the wallet adapters you want to support
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ],
    []
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <SolanaWalletContextProvider>
          {children}
        </SolanaWalletContextProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}; 